import { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { NoteDisclosure } from '@/components/Disclosure';
import { Hint } from '@/components/Hint';
import { StatusPill } from '@/components/Status';
import type { OrdersOverview, OverviewReceiving } from '@/api/orders';
import { ProductCell } from './ProductLink';
import { money } from './lib';
import { AcotReceivingLink, RowAction, WorkEmpty, WorkRow, WorkSection, WorkTable } from './workLib';

interface MoveLine {
  identity: string;
  desc: string | null;
  pid: number | null;
  itemnumber: string | null;
  vendorSku: string | null;
  qty: number;
  value: number | null;
}

interface MoveGroup {
  key: string;
  from: { receivingId: number; vendor: string };
  to: { receivingId: number; vendor: string };
  lines: MoveLine[];
}

/**
 * The move list: lines booked onto the wrong receiving, grouped into literal
 * instructions — "move these N lines from #A to #B" — with both receivings
 * linked into the acot backend where the move actually happens. Nothing to
 * record here: the next re-mirror sees the moved lines and clears both sides.
 */
export function MoveView({ overview, onOpenJourney }: {
  overview: OrdersOverview;
  onOpenJourney: (orderId: string) => void;
}) {
  const { groups, unpaired } = useMemo(() => {
    const groupMap = new Map<string, MoveGroup>();
    const unpairedRows: Array<{
      rcv: OverviewReceiving; desc: string | null; lineKey: string;
      /** true = the server flagged it by brand, nobody has ruled on it yet. */
      flagged: boolean;
      /** The recorded disposition note — the WHY behind the move. */
      note: string | null;
      /** The recorded destination receiving (structured field on the
       *  disposition — never parsed out of the note). */
      dest: number | null;
    }> = [];

    for (const rcv of overview.receivings) {
      for (const d of rcv.discrepancies) {
        if (d.action !== 'move' || d.resolutionStatus === 'resolved') continue;
        const cp = d.misfiledCounterpart;
        if (!cp) continue;
        // Normalise both sides of the pair onto one instruction: the units sit
        // wherever the surplus is, and move toward the short side — so the same
        // physical move, seen from either receiving, lands in the same group.
        const missingHere = d.kind === 'short' || d.kind === 'not_received';
        const from = missingHere
          ? { receivingId: cp.receivingId, vendor: cp.vendor }
          : { receivingId: rcv.receivingId, vendor: rcv.vendor };
        const to = missingHere
          ? { receivingId: rcv.receivingId, vendor: rcv.vendor }
          : { receivingId: cp.receivingId, vendor: cp.vendor };
        const key = `${from.receivingId}>${to.receivingId}`;
        if (!groupMap.has(key)) groupMap.set(key, { key, from, to, lines: [] });
        const group = groupMap.get(key)!;
        const identity = d.itemnumber ? `i:${d.itemnumber}` : d.pid != null ? `p:${d.pid}` : `d:${d.desc}`;
        if (group.lines.some((l) => l.identity === identity)) continue;
        group.lines.push({
          identity,
          desc: d.desc,
          pid: d.pid,
          itemnumber: d.itemnumber,
          vendorSku: d.vendorSku,
          qty: Math.abs(d.difference),
          value: d.valueAtTarget,
        });
      }
    }

    // Rows Matt decided were "not this order" without a detected counterpart —
    // the destination is his to know; the source receiving still links.
    const seenUnpaired = new Set<string>();
    for (const r of overview.resolutions) {
      if (r.disposition !== 'excluded' || (r.resolutionStatus ?? 'pending') === 'resolved') continue;
      const rcv = overview.receivings.find((x) => x.receiptId === r.receiptId);
      if (!rcv) continue;
      const paired = rcv.discrepancies.some(
        (d) => d.key === r.lineKey && d.action === 'move' && d.misfiledCounterpart);
      if (paired) continue;
      const line = rcv.discrepancies.find((d) => d.key === r.lineKey);
      seenUnpaired.add(`${r.receiptId}:${r.lineKey}`);
      unpairedRows.push({
        rcv,
        desc: line?.desc ?? r.lineKey,
        lineKey: r.lineKey,
        flagged: false,
        note: r.note,
        dest: r.destinationReceivingId,
      });
    }

    // One-sided misfiles the server spotted by brand: a product of some OTHER
    // maker sitting in this vendor's receiving with no offsetting short to pair
    // against. These auto-settle to excluded/inferred, so without this they show
    // up in no list at all and the receiving reads balanced with a stranger in
    // it. Listed here rather than as a move instruction because there is no
    // detected destination — only "this does not belong".
    for (const rcv of overview.receivings) {
      for (const d of rcv.discrepancies) {
        if (d.cause !== 'brand_outlier' || d.misfiledCounterpart) continue;
        const id = `${rcv.receiptId}:${d.key}`;
        if (seenUnpaired.has(id)) continue;
        seenUnpaired.add(id);
        unpairedRows.push({ rcv, desc: d.desc, lineKey: d.key, flagged: true, note: null, dest: null });
      }
    }

    return {
      groups: [...groupMap.values()].sort((a, b) => b.lines.length - a.lines.length),
      unpaired: unpairedRows,
    };
  }, [overview]);

  if (groups.length === 0 && unpaired.length === 0) {
    return <WorkEmpty>No products waiting to be moved between receivings.</WorkEmpty>;
  }

  return (
    <div>
      {groups.map((g) => {
        const value = g.lines.reduce((a, l) => a + Math.abs(l.value ?? 0), 0);
        const orderId = overview.receivings.find((r) => r.receivingId === g.to.receivingId)?.orderId
          ?? overview.receivings.find((r) => r.receivingId === g.from.receivingId)?.orderId;
        return (
          <WorkSection
            key={g.key}
            title={
              <>
                <span>
                  Move {g.lines.length} product{g.lines.length === 1 ? '' : 's'}
                  {value > 0 && ` (${money(value)})`}
                </span>
                <AcotReceivingLink receivingId={g.from.receivingId} vendor={g.from.vendor} />
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                <AcotReceivingLink receivingId={g.to.receivingId} vendor={g.to.vendor} />
              </>
            }
            actions={orderId && (
              <RowAction variant="quiet" onClick={() => onOpenJourney(orderId)}>Open journey</RowAction>
            )}
          >
            <WorkTable
              cols={[undefined, 'w-16', 'w-28']}
              head={<><th>Item</th><th className="num">Qty</th><th className="num">Value</th></>}
            >
              {g.lines.map((l) => (
                <tr key={l.identity}>
                  <td>
                    <ProductCell desc={l.desc} pid={l.pid} itemnumber={l.itemnumber} vendorSku={l.vendorSku} lines={1} />
                  </td>
                  <td className="num">{l.qty}</td>
                  <td className="num">{l.value == null ? '—' : money(l.value)}</td>
                </tr>
              ))}
            </WorkTable>
          </WorkSection>
        );
      })}

      {unpaired.length > 0 && (
        <WorkSection title="Doesn't belong here" count={unpaired.length}>
          {unpaired.map((row) => (
            <WorkRow
              key={`${row.rcv.receiptId}:${row.lineKey}`}
              scrollKey={`unpaired:${row.rcv.receiptId}:${row.lineKey}`}
              title={<span className="line-clamp-1 font-medium">{row.desc}</span>}
              tone={row.flagged ? 'warning' : undefined}
              detail={row.note ? <NoteDisclosure text={row.note} /> : undefined}
              actions={
                <RowAction variant="quiet" onClick={() => onOpenJourney(row.rcv.orderId)}>Open journey</RowAction>
              }
            >
              {row.flagged && (
                <Hint content="Another maker's product on this vendor's receiving; nothing decided yet.">
                  <StatusPill tone="warning">wrong brand</StatusPill>
                </Hint>
              )}
              <span className="text-xs text-muted-foreground">on</span>
              <AcotReceivingLink receivingId={row.rcv.receivingId} vendor={row.rcv.vendor} />
              {row.dest != null && (
                <>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <AcotReceivingLink receivingId={row.dest} />
                </>
              )}
            </WorkRow>
          ))}
        </WorkSection>
      )}
    </div>
  );
}
